import axios from "axios";
import {
  Content,
  KnowledgeContent,
  KnowledgeType,
  Numerical,
  Page,
  Quiz,
  QuestionType,
  CourseCategory,
  Course,
  BlogListItem,
  CourseForOverview,
  UserInfo,
  Xps,
  VipInfo,
  UserConfig,
  PurchaseStatuses,
  PurchaseStatusCode,
} from "../types";
import { LOGIN_EVENT } from "../constants";
import { CoachRunDataSource, CoachRunResult, CodeFile } from "@quark/design";
import _ from "lodash";
import { Answer, Option } from "@quark/shared-types";
import {
  ExamDetail,
  ExamErrorDetail,
  ExamListItem,
  ExamLog,
  ExamQuizSubmit,
} from "../types/exam";
import { WechatLoginQrCode, WechatRegister } from "../types/wechat";
import { ChallengePrompt, CodePrompt } from "../types/code";
import { CoursePoster, InviteCourse, Wallet } from "../types/invite";
import { Season } from "../types/season";
import { Upload } from "../types/upload";
import { PosterInfo, Snapshot } from "../types/snapshot";
import {
  ChallengeAnswer,
  ChallengeContext,
  ChallengeList,
  ChallengeLog,
  ChallengeProgress,
  ChallengeRank,
  ChallengeStatus,
  ChallengeTag,
  Difficulty,
} from "../types/Challenge";
import {
  updateAvatar,
  updateSegments,
  updateNickname,
} from "@quark/customer-service";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = "//edu.py2fun.com/";
axios.defaults.timeout = 10000;
axios.interceptors.response.use(
  (response) => {
    if (!response.data) {
      return Promise.reject("服务器错误~!");
    }
    const { code, data, msg } = response.data;
    if (code === 0) {
      return data;
    }
    return Promise.reject(msg);
  },
  (error) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          window.dispatchEvent(new Event(LOGIN_EVENT));
          return Promise.reject("需要登录");
      }
    }
    return Promise.reject(error);
  }
);

interface LoginParams {
  name: string;
  pwd: string;
}
export const postLogin = (params: LoginParams) => {
  return axios.post("//api.py2fun.com/api/pytip/login", params);
};

// === wechat login ===
export const getWechatLoginQrcode = (): Promise<WechatLoginQrCode> => {
  return axios.post("//api.py2fun.com/api/pytip/wechat_pub_login", {
    url: window.location.href,
  });
};

export const checkWechatLoginStatus = (token: string) => {
  return axios.get("//api.py2fun.com/api/pytip/wechat_pub_login_check", {
    params: {
      token,
    },
  });
};

export const registerWechatLogin = (token: string): Promise<WechatRegister> => {
  return axios.post("//api.py2fun.com/api/pytip/wechat_pub_register", {
    token,
  });
};

// === wechat login end ===
let cacheUserInfo: UserInfo | null = null;
export const getUserInfo = async (): Promise<UserInfo> => {
  if (cacheUserInfo !== null) {
    return Promise.resolve(cacheUserInfo);
  }
  const data = (await axios.get("/api/user/profile")) as UserInfo;
  if (data?.is_login) {
    cacheUserInfo = data;
    updateAvatar(data.avatar);
    updateNickname(data.nickname);
    updateSegments(data);
  }
  return data;
};

export const getUserConfig = (): Promise<UserConfig> => {
  return axios.get("/api/user/config");
};

export const updateUserConfig = (config: UserConfig): Promise<UserConfig> => {
  return axios.post("/api/user/config", config);
};

// 获取积分
export const getXps = (): Promise<Xps> => {
  return axios.get("/api/user/xps");
};

export const getPage = (
  courseId: Numerical,
  pageId: Numerical
): Promise<Page> => {
  return axios
    .get(`/api/course/${courseId}/page/${pageId}`)
    .then((data: Record<string, any>) => {
      return {
        id: data.id,
        title: data.title,
        summary: data.summary,
        tags: data.tags,
        status: data.status,
        components: data.components.map((component: Record<string, any>) => {
          if (
            component.type === "QuizRef" ||
            component.type === "QuestionRef"
          ) {
            const content = component.content as Record<string, any>;
            return {
              type: "QuizRef",
              content: {
                version: content.version,
                componentId: content.comp_id,
                title: content.title,
                questionId: content.question_id,
                status: content.learn_status,
              } as Content<"QuizRef">,
            };
          }
          if (component.type === "Knowledge") {
            const content = component.content as Record<string, any>;
            return {
              type: "Markdown",
              content: {
                version: content.version,
                componentId: content.comp_id,
                title: content.title,
                knowledgeId: content.knowledge_id,
                status: content.learn_status,
              } as Content<"Markdown">,
            };
          }
          if (component.type === "ChallengeRef") {
            const content = component.content as Record<string, any>;
            return {
              type: "ChallengeRef",
              content: {
                version: content.version,
                componentId: content.comp_id,
                title: content.title,
                challengeId: content.challenge_id,
                status: content.learn_status,
              } as Content<"ChallengeRef">,
            };
          }
          return {};
        }),
      };
    });
};

export const getKnowledge = (knowledgeId: Numerical): Promise<Quiz> => {
  return axios
    .get(`/api/knowledge/${knowledgeId}`)
    .then((data: Record<string, any>) => {
      return {
        id: data.knowledge_id,
        title: data.title,
        variant: "knowledge",
        knowledges: data.knowledges.map((knowledge: Record<string, any>) => ({
          type: knowledge.type as KnowledgeType,
          step: knowledge.step,
          content: transferQuestionKnowledgeContent(
            knowledge.type as KnowledgeType,
            knowledge.content as Record<string, any>
          ),
        })),
      };
    });
};

export const getQuizRef = (questionId: Numerical): Promise<Quiz> => {
  return axios
    .get(`/api/challenge/quiz/v2/${questionId}`)
    .then((data: Record<string, any>) => {
      const options = data.question_options.map(transferQuestionOption);
      const quizAnswer: Answer[] = _.sortBy(
        options
          .filter((o: any) => o.matchNumber > 0 || o.correct === true)
          .map((o: any) => ({
            id: o.id,
            number: o.matchNumber,
            text: o.text,
          })),
        "number"
      );

      const quiz: Quiz = {
        id: data.question_id,
        title: data.title,
        variant: "quiz",
        question: {
          type: data.question_type as QuestionType,
          text: data.question_text,
          isRight: data.is_right,
          userAnswer: data.user_answers || [],
          quizAnswer: quizAnswer,
          options: options,
        },
      };

      if (data.knowledges) {
        quiz.knowledges = data.knowledges.map(
          (knowledge: Record<string, any>) => ({
            type: knowledge.type as KnowledgeType,
            step: knowledge.step,
            content: transferQuestionKnowledgeContent(
              knowledge.type as KnowledgeType,
              knowledge.content as Record<string, any>
            ),
          })
        );
      }
      return quiz;
    });
};

export const getChallengeRef = (id: Numerical): Promise<CoachRunDataSource> => {
  return axios.get(`/api/challenge/code/${id}`);
};

export const getVipInfo = (): Promise<VipInfo> => {
  return axios.get("/api/vip_course");
};

interface SubmitChallengeParams {
  challenge_id: Numerical;
  source_code: string;
  language: string;
}
interface SubmitChallengeResp extends CoachRunResult {}
export const submitChallenge = (
  params: SubmitChallengeParams
): Promise<SubmitChallengeResp> => {
  return axios.post("/api/challenge/code/v2/submit", params);
};

export interface CheckCodeHasSdtinResp {
  has_stdin: true;
}
export const checkCodeHasSdtin = (
  language: string,
  source_code: string
): Promise<CheckCodeHasSdtinResp> => {
  const params = {
    language,
    source_code,
  };
  return axios.post("/api/course/code/has_stdin", params);
};

interface ExecCodeParams {
  language: string;
  source_code: string;
  additional_files: string;
  entry_filename: string;
  time_limit: number;
  stdin: string;
  course_no?: string;
  page_id?: string | number;
  comp_id?: string;
}
export interface ExecCodeResp {
  format: "sql_json" | undefined;
  stdin?: string;
  status: number; // 0 执行正确，300 执行出错
  stderr: string; // 错误输出
  stdout: string; // 正常输出
  reason: string; // 提示，目前为 Succeeded or Failed
  time: string; // 执行时长，如 1.8s 之类的
  files: CodeFile[];
}
export const execCode = (params: ExecCodeParams): Promise<ExecCodeResp> => {
  return axios.post("/api/course/code/execute", params);
};

export const getCourseCategories = (): Promise<CourseCategory[]> => {
  return axios.get("/api/home_courses");
};

interface MyCourseResp {
  my_courses: Course[];
  recommend_courses: Course[];
}
export const getMyCourses = (): Promise<MyCourseResp> => {
  return axios.get("/api/my_courses");
};

interface BlogListParams {
  page?: number;
  page_size?: number;
  search?: string;
}
interface BlogListResp {
  count: number;
  results: BlogListItem[];
  previous: string | null;
  next: string | null;
}
export const getBlogList = (
  params: BlogListParams = { page: 1, page_size: 9, search: "" }
): Promise<BlogListResp> => {
  return axios.get("/api/blogs", { params });
};

interface ExamListParams {
  page?: number;
  page_size?: number;
}
interface ExamListResp {
  count: number;
  results: ExamListItem[];
  previous: string | null;
  next: string | null;
}

// == exam start ==
interface ExamListParams {
  page?: number;
  page_size?: number;
}
interface ExamListResp {
  count: number;
  results: ExamListItem[];
  previous: string | null;
  next: string | null;
}
export const getExamList = (
  params: ExamListParams = { page: 1, page_size: 20 }
): Promise<ExamListResp> => {
  return axios.get("/api/exams", { params });
};

export const startExam = (id: number): Promise<ExamLog> => {
  return axios.post(`/api/exam/${id}/start`);
};

export const getExamDetail = (id: number): Promise<ExamDetail> => {
  return axios.get(`/api/exam/${id}`);
};

export const getExamLogDetail = (
  id: number,
  logId: number
): Promise<ExamDetail> => {
  return axios.get(`/api/exam/${id}/${logId}/detail`);
};

export const getExamErrorBook = (): Promise<ExamErrorDetail> => {
  return axios.get("/api/err_books?err_type=exam_quiz");
};

export const removeExamErrorBookItem = (id: number) => {
  return axios.post("/api/err_book/remove", { id });
};

// 提交单个quiz答案
export const submitExamQuiz = (params: ExamQuizSubmit) => {
  return axios.post(`/api/challenge/quiz/v2/submit`, params);
};

// 提交整个exam
export const submitExam = (id: number, logId: number): Promise<ExamLog> => {
  return axios.post(`/api/exam/${id}/${logId}/submit`);
};
// == exam end ==

const transferQuestionOption = (dataSource: Record<string, any>): Option => {
  return {
    id: dataSource.id,
    key: dataSource.key,
    text: dataSource.text,
    correct: dataSource.correct,
    maxLength: dataSource.max_length,
    matchNumber: dataSource.match_number,
  };
};

const transferQuestionKnowledgeContent = (
  type: KnowledgeType,
  dataSource: Record<string, any>
): KnowledgeContent<KnowledgeType> => {
  if (type === "MarkdownEditor") {
    return {
      id: dataSource.comp_id,
      title: dataSource.title,
      text: dataSource.text,
    };
  }
  if (type === "Note") {
    return {
      id: dataSource.comp_id,
      text: dataSource.text,
    };
  }
  if (type === "Code") {
    return {
      id: dataSource.comp_id,
      title: dataSource.title,
      language: dataSource.language,
      specialInput: dataSource.special_input,
      entryFileName: dataSource.entry_file_name,
      content: dataSource.content,
      shortContent: dataSource.short_content,
      timeLimit: dataSource.time_limit,
      allowDownload: dataSource.allow_download,
      readonly: dataSource.read_only,
      highlightLines: dataSource.high_lighted_lines,
      runnable: dataSource.runnable,
      judge: dataSource.judge,
      judgeContentPrepend: dataSource.judge_content_prepend,
      showSolution: dataSource.show_solution,
      solutionContent: dataSource.solution_content,
      additionalContent: dataSource.additional_content || [],
      judgeContent: dataSource.judge_content,
      judgeHints: dataSource.judge_hints,
    };
  }
  if (type === "Image") {
    return {
      src: dataSource.file,
      width: dataSource.metadata?.width || 0,
      height: dataSource.metadata?.height || 0,
    };
  }
  if (type === "JsHtml") {
    return {
      htmlCode: dataSource.html_code,
      jsCode: dataSource.js_code,
      cssCode: dataSource.css_code,
      language: dataSource.language,
      runnable: dataSource.runnable,
      showOutput: dataSource.show_output,
    };
  }
  // 默认返回Coach
  return {
    id: dataSource.challenge_id,
    name: dataSource.name,
    description: dataSource.description,
    title: dataSource.title,
  };
};

export const submitUserAnswer = (id: Numerical, data: any) => {
  return axios.post("/api/challenge/quiz/v2/submit", {
    question_id: id,
    user_answers: data,
  });
};

export const getCourseOverView = (
  courseNo: string
): Promise<CourseForOverview> => {
  return axios
    .get(`/api/course/${courseNo}/overview`)
    .then((data: Record<string, any>) => {
      return {
        id: data.id,
        no: data.course_no,
        title: data.title,
        summary: data.details,
        courseIcon: data.mini_cover_img,
        widgets: {
          lesson: data.widget_stats?.lesson,
          quiz: data.widget_stats?.quiz,
          challenge: data.widget_stats?.challenge,
          playground: data.widget_stats?.playground,
          illustration: data.widget_stats?.illustration,
        },
        progress: {
          totalPageNumber: data.learn_info?.page_num,
          learnPageNumber: data.learn_info?.learn_page_num,
          totalCoachNumber: data.learn_info?.coach_num,
          learnCoachNumber: data.learn_info?.learn_coach_num,
        },
        isBuy: data.is_buy,
        categories: data.toc?.categories.map(
          (category: Record<string, any>) => ({
            id: category.id,
            title: category.title,
            type: category.type,
            icon: category.icon_url,
            coaches: category.coaches?.map((coach: Record<string, any>) => ({
              id: coach.id,
              title: coach.title,
              summary: coach.sub_title,
              isPreview: coach.is_preview,
              status: coach.status,
              number: coach.number,
            })),
            pages: category.pages?.map((page: Record<string, any>) => ({
              id: page.id,
              title: page.title,
              summary: page.sub_title,
              isPreview: page.is_preview,
              status: page.status,
              number: page.number,
              coaches: page.coaches?.map((coach: Record<string, any>) => ({
                id: coach.id,
                title: coach.title,
                summary: coach.sub_title,
                isPreview: coach.is_preview,
                status: coach.status,
                number: coach.number,
              })),
            })),
          })
        ),
      };
    });
};

export const submitSlideProgress = (
  courseNo: string,
  pageId: string,
  compId: string
) => {
  axios
    .post(`/api/course/${courseNo}/submit_page_progress`, {
      status: "done",
      page_id: pageId,
      comp_id: compId,
    })
    .then(() => {});
};

export const submitPageProgress = (courseNo: string, pageId: string) => {
  axios
    .post(`/api/course/${courseNo}/submit_progress`, {
      status: "done",
      page_id: pageId,
    })
    .then(() => {});
};

export const unlockQuestion = (questionId: Numerical): Promise<void> => {
  return axios.post(`/api/challenge/quiz/v2/${questionId}/unlock`);
};

export const isFirstTimeCompleted = (
  courseId: string,
  pageId: string
): Promise<boolean> => {
  const key = `completed-${courseId}-${pageId}`;
  if (window.localStorage.getItem(key) === "completed") {
    return Promise.resolve(false);
  }
  window.localStorage.setItem(key, "completed");
  return Promise.resolve(true);
};

export const getCertificationShareKey = (courseNo: string): Promise<string> => {
  return axios
    .post("/api/course_certiy", {
      course_no: courseNo,
    })
    .then((data: Record<string, any>) => {
      return data.share_key;
    });
};

// chatgpt api
export const genCodePrompt = (
  prompt: CodePrompt
): Promise<{ prompt: string }> => {
  return axios.post("/api/chat/code/prompt", prompt);
};

export const genChallengePrompt = (
  prompt: ChallengePrompt
): Promise<{ prompt: string }> => {
  return axios.post("/api/chat/challenge/prompt", prompt);
};

export const replyApiUrl = "//edu.py2fun.com/api/chat/tbot/reply";
// chatapt api end

// == 邀请返现 ==
export const getInviteCourses = (): Promise<InviteCourse[]> => {
  return axios.get("/api/invite/courses");
};

export const genInviteCource = (course_no: string): Promise<CoursePoster> => {
  return axios.post("/api/invite/create", { course_no, invite_type: "buy" });
};

export const getMyWallet = (): Promise<Wallet> => {
  return axios.get("/api/user/wallet");
};
// == 邀请返现 end ==

// == 赛季 ==
export const getSeasons = (): Promise<Season.Season[]> => {
  return axios.get("/api/seasons");
};

export const getSeason = (season_id: number): Promise<Season.Season> => {
  return axios.get("/api/season", {
    params: { season_id },
  });
};

export const postTeamRegister = (
  season_id: number,
  match_id: number
): Promise<Season.TeamRegisterInfo> => {
  return axios.post("/api/season/team/register", { season_id, match_id });
};

export const getSeasonTasks = (season_id: number): Promise<Season.Task[]> => {
  return axios.get("/api/season/tasks", {
    params: { season_id },
  });
};

export const postSubTaskPunch = (
  sub_task_id: number,
  attachment: string
): Promise<{}> => {
  return axios.post("/api/season/task/upload_attachment", {
    sub_task_id,
    attachment,
  });
};

export const postAcquireTaskReward = (task_id: number): Promise<{}> => {
  return axios.post("/api/season/task/scholarship_acquire", { task_id });
};
// == 赛季 end ==

// == 文件上传 ==
export const postUploadFile = (file: File): Promise<Upload.File> => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post("/api/assets/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// 保存截图
type SnapshotData = {
  course_no: string;
  page_id: number;
  step: number;
  file: string;
};
export const postTakeSnapshot = (data: SnapshotData): Promise<{}> => {
  return axios.post("/api/user/snapshots", data);
};

// 获取截图
export const getSnapshots = (params: {
  course_no: string;
  page_id: number;
}): Promise<Snapshot[]> => {
  return axios.get("/api/user/snapshots", { params });
};

// 海报信息
export const getPosterInfo = (params: {
  course_no: string;
  page_id: number;
}): Promise<PosterInfo> => {
  return axios.get("/api/user/poster/info", { params });
};

export type GetAllChallengeParams = {
  page?: number;
  pageSize?: number;
  difficulties?: Difficulty[];
  tags?: string[];
  status?: ChallengeStatus[];
  search?: string;
  catalog?: string;
};

// 获取挑战题目列表
export const getAllChallenges = ({
  page = 1,
  pageSize = 40,
  difficulties,
  status,
  tags,
  catalog = "all",
  ...others
}: GetAllChallengeParams): Promise<ChallengeList> => {
  return axios.get(`/api/problemset/${catalog}/challenges`, {
    params: {
      page,
      page_size: pageSize,
      difficulties: difficulties?.join(","),
      status: status?.join(","),
      tags: tags?.join(","),
      ...others,
    },
  });
};

// 获取当前用户挑战进度
export const getChallengeProgress = (
  catalog: string
): Promise<ChallengeProgress> => {
  return axios.get(`/api/problemset/${catalog}/progress`);
};

// 获取题库动态筛选标签
export const getChallengeDynamicTags = (
  catalog: string
): Promise<ChallengeTag[]> => {
  return axios
    .get(`/api/problemset/${catalog}/filters`)
    .then((data: Record<string, any>) => {
      return data.tags;
    });
};

// 获取挑战题目提交记录
export const getChallengeLogs = (
  challengeId: number
): Promise<ChallengeLog[]> => {
  return axios.get(`/api/challenge/code/${challengeId}/logs`);
};

// 获取挑战题目上下题目
export const getChallengeContext = (
  setType: string,
  challengeId: number
): Promise<ChallengeContext> => {
  return axios.get(`/api/problemset/${setType}/${challengeId}/context_ids`);
};

// 获取题库挑战排名
export const getChallengeRank = (
  type: "day" | "week" | "all"
): Promise<ChallengeRank> => {
  return axios.get("/api/rank/problemset", {
    params: {
      type,
      count: 10,
    },
  });
};

export const getChallengeCalendar = async (
  month: string
): Promise<Record<string, any>[]> => {
  const data = await axios.get("/api/user/calendar", {
    params: { month },
  });
  return (data as any).calendar;
};

export const getChallengeWechatGroupQrcode = async (): Promise<string> => {
  const data = await axios.get("/api/wxgroup/join_img?key=problemset_group");
  return (data as any).img;
};

export const getChallengeAnswer = (
  challengeId: number
): Promise<ChallengeAnswer[]> => {
  return axios.get(`/api/challenge/code/${challengeId}/see_answer`);
};

// 提交订单
export const submitVipOrder = async (vipCode: string) => {
  // 1. 根据vipCode获取商品信息
  // 2. 根据商品信息提交订单
  const productInfo = (await axios.get("/api/mall/vip_items", {
    params: {
      code: vipCode,
    },
  })) as Record<string, any>;

  const { item_id, sub_item_id, price } = productInfo.mall_info;
  const searchParams = new URLSearchParams(window.location.search);

  let items: string;
  if (import.meta.env.DEV) {
    items = JSON.stringify([
      {
        item_id: 5,
        sub_item_id: 5,
        count: 1,
      },
    ]);
  } else {
    items = JSON.stringify([
      {
        item_id,
        sub_item_id,
        count: 1,
      },
    ]);
  }

  const orderInfo = (await axios.post("/api/mall/submit_order", {
    items,
    buy_from: searchParams.get("buy_from") || undefined,
    pay_type: 204,
  })) as Record<string, any>;
  return {
    price,
    orderUUID: orderInfo.order_uuid,
    codeURL: orderInfo?.pay_request?.codeUrl,
  };
};

export const getOrderStatus = async (
  orderUUID: string,
  controller: AbortController
) => {
  const orderInfo = (await axios.get("/api/mall/order_status", {
    params: {
      order_uuid: orderUUID,
    },
    signal: controller.signal,
  })) as Record<string, any>;
  const statusCode = (orderInfo.status || 100) as PurchaseStatusCode;
  const status = PurchaseStatuses[statusCode];
  if (status === "CANCELLED" || status === "FINISHED") {
    return status;
  }
  return new Promise<"CANCELLED" | "FINISHED">((resolve) => {
    setTimeout(() => {
      resolve(getOrderStatus(orderUUID, controller));
    }, 2000);
  });
};
